import { AlertTriangle, Check, ClipboardList, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { ApiError, api } from "@/lib/api";
import { errorMessage, positionWord } from "@/lib/format";
import {
  type CreateItem,
  type Platform,
  createRefusal,
  createUnits,
  shelfName,
  tasksHref,
} from "@/lib/podsort";
import type { PodsortCreated, PodsortRow } from "@/lib/providers";

/**
 * ОКНО «СОЗДАТЬ ЗАДАЧИ ПОДСОРТА» — отмеченное в расчёте уезжает в работу
 * сборщику (задача владельца 17.09.26).
 *
 * Задачи ставятся в ЧУЖУЮ базу (`fbs.fbs_podsort_task`), и с этой секунды их
 * видит сборщик FBS своего города. Поэтому окно, а не щелчок: промах по кнопке
 * не должен значить «пошлите людей за товаром».
 *
 * ОКНО ПОКАЗЫВАЕТ РОВНО ТО, ЧТО УЕДЕТ: площадку, город и по строке на позицию с
 * числом «Собрать». Это же число сервер пересчитает своим расчётом и, если оно
 * устарело, откажет ЦЕЛИКОМ, ничего не создав (`need_changed`) — тогда экран
 * перечитывает список, и человек видит новые числа.
 *
 * Ключа однократности нет, и он не нужен: поставленная задача сама уменьшает
 * потребность, и второе нажатие получает тот же отказ, не записав ни строки.
 */
export function PodsortCreateSheet({
  platform,
  city,
  norm,
  keep,
  rows,
  items,
  onClose,
  onDone,
}: {
  platform: Platform;
  city: { city_id: number; name: string };
  norm: number;
  keep: number;
  /** Отмеченные строки — чтобы назвать товар в окне. */
  rows: readonly PodsortRow[];
  items: readonly CreateItem[];
  onClose: () => void;
  /** Что-то записано либо расчёт устарел — список надо перечитать. */
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState("");
  const [done, setDone] = useState<PodsortCreated | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const units = createUnits(items);
  const byCard = new Map(rows.map((r) => [r.card?.card_id ?? 0, r]));

  async function send() {
    setBusy(true);
    setFailed("");
    try {
      const reply = await api.post<PodsortCreated>("/api/podsort/tasks", {
        platform,
        city_id: city.city_id,
        norm,
        keep,
        items,
      });
      setDone(reply);
      onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        setFailed(createRefusal(err.message));
        // Устаревший расчёт перечитывается сразу: окно говорит «проверьте
        // числа», и за ним обязан стоять уже новый список.
        if (err.message === "need_changed") onDone();
      } else {
        setFailed(errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Создать задачи подсорта"
      data-annot="окно создания задач"
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={busy ? undefined : onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-full w-full max-w-[520px] flex-col gap-3 overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-elevated p-4"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-[14px] font-semibold">
            Задачи подсорта · {shelfName(platform)}
            <span className="ml-1.5 text-[12.5px] font-normal text-fg-muted">
              {city.name}
            </span>
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Закрыть"
            data-annot="закрыть"
            className="flex h-7 w-7 flex-none items-center justify-center rounded-[var(--radius-ui)] border border-border text-fg-muted hover:text-fg"
          >
            <X size={14} />
          </button>
        </div>

        {done ? (
          <div data-annot="итог создания" className="flex flex-col gap-3">
            <p className="flex items-start gap-2 rounded-[var(--radius-ui)] border border-ok/50 bg-bg-subtle px-2.5 py-2 text-[13px]">
              <Check size={15} className="mt-px flex-none text-ok" />
              <span>
                Создано задач: <b className="tnum">{done.created}</b> по{" "}
                <span className="tnum">{done.positions}</span>{" "}
                {positionWord(done.positions)}. Сборщики {city.name} видят их в
                FBS, в разделе «Подсорт».
              </span>
            </p>
            {/* Номера — единственное, чем человек найдёт эти задачи потом. */}
            <p className="text-[12px] text-fg-muted">
              Номера:{" "}
              <span className="tnum text-fg">
                {done.task_ids.length > 12
                  ? `${done.task_ids.slice(0, 12).join(", ")} … ещё ${done.task_ids.length - 12}`
                  : done.task_ids.join(", ")}
              </span>
            </p>
            {done.signed ? null : (
              <p className="text-[12px] text-err">
                Подпись автора не записалась — задачи созданы, но в списке будут
                без автора.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Link
                to={tasksHref(platform, city.city_id)}
                data-annot="к задачам"
                className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover"
              >
                <ClipboardList size={13} />
                Открыть задачи
              </Link>
              <button
                type="button"
                onClick={onClose}
                className="h-8 rounded-[var(--radius-ui)] border border-accent/60 bg-accent-dim px-3 text-[13px] font-medium text-accent-text"
              >
                Готово
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-[12.5px] text-fg-muted">
              {items.length} {positionWord(items.length)} ·{" "}
              <span className="tnum font-medium text-fg">{units} шт</span> · по{" "}
              {norm} шт на позицию, в коробах остаётся {keep} шт
            </p>

            <ul
              data-annot="что уедет"
              className="min-h-0 flex-1 divide-y divide-border overflow-y-auto rounded-[var(--radius-ui)] border border-border"
            >
              {items.map((item) => {
                const row = byCard.get(item.card_id);
                return (
                  <li
                    key={item.card_id}
                    className="flex items-center gap-2.5 px-2.5 py-1.5 text-[12.5px]"
                  >
                    <span className="flex h-[36px] w-[27px] flex-none items-center justify-center overflow-hidden rounded border border-border bg-bg-subtle">
                      {row?.card?.image_url ? (
                        <img
                          src={row.card.image_url}
                          alt=""
                          referrerPolicy="no-referrer"
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="tnum block">{row?.barcode ?? item.card_id}</span>
                      <span className="block truncate text-[11px] text-fg-muted">
                        {row?.card?.size ? `${row.card.size} · ` : ""}
                        {row?.card?.name ?? ""}
                      </span>
                    </span>
                    <span className="tnum font-semibold">{item.count} шт</span>
                  </li>
                );
              })}
            </ul>

            <p className="flex items-start gap-2 rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-2.5 py-2 text-[12px] text-fg-muted">
              <AlertTriangle size={14} className="mt-px flex-none" />
              <span>
                Задачи сразу увидят сборщики города в FBS под вашей подписью.
                Каждая штука — отдельная задача. Отменяют задачи в FBS.
              </span>
            </p>

            {failed ? (
              <p
                role="alert"
                data-annot="отказ"
                className="rounded-[var(--radius-ui)] border border-err/60 bg-err-bg px-2.5 py-2 text-[12.5px]"
              >
                {failed}
              </p>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={send}
                disabled={busy || items.length === 0}
                data-annot="создать"
                className="h-8 rounded-[var(--radius-ui)] border border-accent/60 bg-accent-dim px-3 text-[13px] font-medium text-accent-text disabled:opacity-55"
              >
                {busy ? "Создаю…" : `Создать ${units} шт`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
